import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Utensils, ArrowRight, AlertTriangle, Check } from 'lucide-react';
import api from '../services/api';

/**
 * NutritionBaselineSection — read-only summary of the user's nutrition baseline
 * + a single CTA to edit it via chat (where the full form lives). One form,
 * two entry points (chat + settings) so we don't duplicate UI.
 */

const NutritionBaselineSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.getNutritionProfile();
      setData(r.data || null);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('wellness:refresh', h);
    return () => window.removeEventListener('wellness:refresh', h);
  }, [load]);

  if (loading) return null;

  const status = data?.status;
  const baseline = data?.baseline || {};
  const targets = data?.targets;
  const isSet = !!status?.is_set;

  return (
    <div className="hpme-card p-6" data-testid="settings-nutrition-baseline">
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-secondary font-bold text-xl flex items-center gap-2">
          <Utensils className="w-5 h-5" style={{ color: '#fb923c' }} />
          NUTRITION BASELINE
        </h3>
        <Link
          to="/chat?intent=nutrition_baseline"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 hover:border-primary text-xs font-secondary font-bold uppercase tracking-wider rounded-sm transition-colors"
          data-testid="settings-edit-baseline-link"
        >
          {isSet ? 'Edit via chat' : 'Set targets'} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {!isSet ? (
        <p className="text-sm text-zinc-400 leading-relaxed flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-zinc-500" />
          No baseline yet — without your stats Nikki can't compute realistic calorie or protein targets. Tap "Set targets" to start.
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-zinc-400 flex items-center gap-2">
            <Check className="w-4 h-4 text-primary" /> Targets active — Nikki uses these on every chat turn.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Goal</p>
              <p className="font-secondary font-bold text-sm text-white capitalize mt-1">{baseline.goal || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Activity</p>
              <p className="font-secondary font-bold text-sm text-white capitalize mt-1">{baseline.activity_level || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Meals/day</p>
              <p className="font-secondary font-bold text-sm text-white mt-1">{baseline.meal_frequency || '—'}</p>
            </div>
          </div>
          {targets && (
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-zinc-800">
              <div className="border-l-2 border-primary pl-3"><p className="text-[10px] uppercase text-zinc-500">CALORIES</p><p className="font-secondary font-bold text-lg text-primary">{targets.daily_kcal_target}</p></div>
              <div className="border-l-2 border-accent-purple pl-3"><p className="text-[10px] uppercase text-zinc-500">PROTEIN</p><p className="font-secondary font-bold text-lg text-accent-purple">{targets.protein_target_g}g</p></div>
              <div className="border-l-2 border-zinc-600 pl-3"><p className="text-[10px] uppercase text-zinc-500">CARBS · FAT</p><p className="font-secondary font-bold text-xs text-zinc-300 mt-1">{targets.carb_target_g}g · {targets.fat_target_g}g</p></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NutritionBaselineSection;
